import useFeedInfiniteScroll from "@/shared/hooks/useFeedInfiniteScroll";
import { PaginationResponse } from "@/shared/types/api.types";
import { cn } from "@/shared/utils";
import {
  InfiniteData,
  UseInfiniteQueryResult,
  UseSuspenseInfiniteQueryResult,
} from "@tanstack/react-query";
import { HTMLAttributes, ReactElement, ReactNode } from "react";

export interface InfiniteScrollListProps
  extends Omit<HTMLAttributes<HTMLDivElement>, "placeholder"> {
  children: ReactNode;
  placeholder?: ReactNode;
  className?: string;
  canFetchMore: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => Promise<unknown>;
}

export const checkFetchMore = (
  // (Specific since we don't know inferred type)

  queryResult:
    | UseInfiniteQueryResult<InfiniteData<PaginationResponse<any>>>
    | UseSuspenseInfiniteQueryResult<InfiniteData<PaginationResponse<any>>>
): boolean =>
  !queryResult?.isLoading &&
  !queryResult?.isFetchingNextPage &&
  queryResult?.hasNextPage &&
  !!queryResult?.data &&
  queryResult?.data.pages.length > 0;

function InfiniteScrollList({
  children,
  placeholder,
  className,
  canFetchMore,
  isFetchingNextPage,
  fetchNextPage,
  ...props
}: InfiniteScrollListProps): ReactElement {
  const infiniteScrollRef = useFeedInfiniteScroll({
    fetchPage: fetchNextPage,
    canFetchMore,
  });

  return (
    <div {...props} className={cn("relative flex flex-col", className)}>
      {children}
      {isFetchingNextPage && placeholder}
      <div
        className="pointer-events-none absolute bottom-0 left-0 h-px w-px opacity-0"
        ref={infiniteScrollRef}
      />
    </div>
  );
}

export default InfiniteScrollList;
